import { useAbility } from "@casl/react";
import { useEffect, useMemo, useState } from "react";
import LocationSelect from "./LocationSelect";
import useAuth from "hooks/UseAuth";
import { AbilityContext } from "utils/abilit";
import { PermissionType, SubjectKey } from "utils/enums";

type LocationSelectProps = React.ComponentProps<typeof LocationSelect>;

const UserBasedLocationSelect: React.FC<LocationSelectProps> = ({
  isForm,
  value: originalValue,
  onChange,
  selectProps,
  ...rest
}) => {
  const ability = useAbility(AbilityContext);
  const auth = useAuth();
  const [userLocation, setUserLocation] = useState<number | null>(null);
  const [hasLocationReadPermission, setHasLocationReadPermission] =
    useState(false);

  useEffect(() => {
    const canReadLocation = ability.can(
      PermissionType.READ,
      SubjectKey.LOCATION
    );
    let locationId: number | null = null;

    const userDetails = auth?.userDetails;

    if (userDetails) {
      try {
        const parsedDetails = JSON.parse(userDetails);
        const parsedLocation = parsedDetails?.location;
        locationId = typeof parsedLocation === "number" ? parsedLocation : null;
      } catch (error) {
        console.error("Failed to parse user details", error);
      }
    }

    setHasLocationReadPermission(!!canReadLocation);
    setUserLocation(locationId);
  }, [ability, auth?.userDetails]);

  useEffect(() => {
    if (!hasLocationReadPermission) {
      if (userLocation !== null) {
        if (originalValue !== userLocation) {
          onChange?.(userLocation);
        }
      } else if (originalValue !== null && originalValue !== undefined) {
        onChange?.(null);
      }
    }
  }, [
    hasLocationReadPermission,
    userLocation,
    originalValue,
    onChange,
  ]);

  const shouldDisable = !hasLocationReadPermission && userLocation !== null;

  const mergedSelectProps = useMemo(() => {
    return {
      ...(selectProps || {}),
      disabled: shouldDisable ? true : selectProps?.disabled,
    };
  }, [selectProps, shouldDisable]);

  const value = shouldDisable ? userLocation : originalValue;

  return (
    <LocationSelect
      {...rest}
      isForm={isForm}
      value={value}
      onChange={onChange}
      selectProps={mergedSelectProps}
    />
  );
};

export default UserBasedLocationSelect;
